import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import Page from 'components/util/page-component';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import ConditionalRender from 'components/util/conditional-render-component';
import StatementEditInfo from 'components/feature/statement-edit/statement-edit-statement-info-component';

import selectedProjectApi from 'api/selected-project-api';
import statementCreateEditApi from 'api/statement-creation-edit-api';

import { ReactComponent as BackButton } from 'icons/back-button.svg';
import StatementEditCancelModal from 'components/feature/statement-edit/statement-edit-cancel-modal';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import StatementForm from 'models/form/statement-form-model';
import ProjectApi from 'models/api/project-api-model';
import { withNavigationBlocking } from 'higher-order-components/with-navigation-blocking';

const STATEMENT_EDIT_PAGE_BLOCK = 'statement-edit';
const STATEMENT_EDIT_PAGE_ID_BLOCK = 'statement-edit-id';

class StatementEdit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      project: new ProjectApi(),
      statementInProgress: new StatementForm(),
      statementHasBeenModified: false,
    };
  }

  _fetchStatementEditInitData = async () => {
    const {
      params: { projectId, statementId },
      updateSelectedProjectFromIDIfDoesNotExists,
    } = this.props;
    await updateSelectedProjectFromIDIfDoesNotExists({ projectId });
    this._fetchSelectedProjectForEdit(projectId);
    this._fetchSelectedStatementForEdit(statementId);
  };

  componentDidMount() {
    this._fetchStatementEditInitData();
  }

  _fetchSelectedProjectForEdit = async (projectId) => {
    this.setState((state) => ({
      project: state.project.setLoading(),
    }));
    try {
      const response = await selectedProjectApi.getSelectedProjectRequest(
        projectId,
      );
      this.setState((state) => ({
        project: state.project.setLoaded(response),
      }));
    } catch (error) {
      this.setState((state) => ({
        project: state.project.setError(error),
      }));
    }
  };

  _fetchSelectedStatementForEdit = async (statementId) => {
    const {
      params: { projectId },
    } = this.props;
    this.setState((state) => ({
      statementInProgress: state.statementInProgress.setLoading(),
    }));
    try {
      const response = await statementCreateEditApi.getSelectedStatementRequest(
        statementId,
        projectId,
      );
      this.setState((state) => ({
        statementInProgress: state.statementInProgress.setLoaded({ response }),
      }));
    } catch (error) {
      this.setState((state) => ({
        statementInProgress: state.statementInProgress.setError(error),
      }));
    }
  };

  _toggleCancelModal = () => {
    const { triggerNavigationConfirm } = this.props;
    triggerNavigationConfirm(ROUTE_CONSTANTS.HOME_PAGE);
  };

  _onCancelAndLeavePage = () => {
    const { navigate, confirmNavigation, disableNavigationBlocking } =
      this.props;
    confirmNavigation();
    disableNavigationBlocking();
    navigate(ROUTE_CONSTANTS.HOME_PAGE);
  };

  _handleModalCancel = () => {
    const { cancelNavigation } = this.props;
    cancelNavigation();
  };

  componentDidUpdate(prevProps, prevState) {
    const { statementInProgress, statementHasBeenModified } = this.state;
    if (
      prevState.statementInProgress.isInitialized() &&
      statementInProgress.hasStatementBeenModified(
        prevState.statementInProgress,
      ) &&
      !statementHasBeenModified
    ) {
      // Set the statement as having been modified if it has been changed at all
      this.setState({
        statementHasBeenModified: true,
      });
    }
  }

  _editStatement = async () => {
    const { statementInProgress, project } = this.state;
    const { disableNavigationBlocking } = this.props;
    this.setState((state) => ({
      statementInProgress: state.statementInProgress.setLoading(),
    }));
    try {
      const response = await statementCreateEditApi.editStatementRequest(
        statementInProgress,
        project,
      );
      this.setState((state) => ({
        statementInProgress: state.statementInProgress.setLoaded({ response }),
      }));
      disableNavigationBlocking();
      this.props.finishStatementEdit({
        project: project.data,
      });
    } catch (error) {
      this.setState((state) => ({
        statementInProgress: state.statementInProgress.setError(error),
      }));
    }
  };

  render() {
    const { showNavigationConfirm } = this.props;
    const { project, statementInProgress, statementHasBeenModified } =
      this.state;
    return (
      <Page>
        <ConditionalRender dependencies={[project, statementInProgress]}>
          <React.Fragment>
            <div className={STATEMENT_EDIT_PAGE_BLOCK}>
              <div className={`${STATEMENT_EDIT_PAGE_BLOCK}__header`}>
                <div>
                  <Button.IconButton
                    id={`${STATEMENT_EDIT_PAGE_ID_BLOCK}__back-button`}
                    className={`${STATEMENT_EDIT_PAGE_BLOCK}__back-button`}
                    type={BUTTON_TYPES.icon}
                    Icon={BackButton}
                    onClick={() => this._toggleCancelModal()}
                  >
                    <div className={`${STATEMENT_EDIT_PAGE_BLOCK}__back-title`}>
                      {project.name}
                    </div>
                  </Button.IconButton>
                  <h1 className={`${STATEMENT_EDIT_PAGE_BLOCK}__page-title`}>
                    <FormattedMessage id={'statement-edit.title'} />
                  </h1>
                </div>
                <div
                  className={`${STATEMENT_EDIT_PAGE_BLOCK}__button-container`}
                >
                  <Button
                    id={`${STATEMENT_EDIT_PAGE_ID_BLOCK}-cancel-button`}
                    className={`${STATEMENT_EDIT_PAGE_BLOCK}__cancel-button`}
                    type={BUTTON_TYPES.secondary}
                    onClick={this._toggleCancelModal}
                  >
                    <FormattedMessage id={'common.cancel'} />
                  </Button>
                  <Button
                    id={`${STATEMENT_EDIT_PAGE_ID_BLOCK}-save-button`}
                    className={`${STATEMENT_EDIT_PAGE_BLOCK}__save-button`}
                    type={BUTTON_TYPES.primary}
                    onClick={this._editStatement}
                    disabled={
                      !statementHasBeenModified ||
                      !statementInProgress.isStepTwoValid()
                    }
                  >
                    <FormattedMessage id={'common.save'} />
                  </Button>
                </div>
              </div>
              <StatementEditInfo
                statementInProgress={statementInProgress}
                setStatementName={(name) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setStatementName(name),
                  }))
                }
                setLanguageType={(lang) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setLanguageType(lang),
                  }))
                }
                setDateFormat={(date) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setDateFormat(date),
                  }))
                }
                setNumberFormat={(number) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setNumberFormat(number),
                  }))
                }
                setCurrency={(currencyId) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setCurrency(currencyId),
                  }))
                }
                setStatementType={(statementType) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setStatementType(statementType),
                  }))
                }
                setQuarter={(val) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setQuarter(val),
                  }))
                }
                setFiscalYear={(year) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setFiscalYear(year),
                  }))
                }
                setPeriodEndingDate={(date) => {
                  if (date instanceof moment) {
                    this.setState((state) => ({
                      statementInProgress:
                        state.statementInProgress.setPeriodEndingDate(date),
                    }));
                  }
                }}
                setAccountingBasis={(option) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setAccountingBasis(option),
                  }))
                }
                setRegistrant={(option) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setRegistrant(option),
                  }))
                }
                setWrapUpDate={(date) => {
                  if (date instanceof moment) {
                    this.setState((state) => ({
                      statementInProgress:
                        state.statementInProgress.setWrapUpDate(date),
                    }));
                  }
                }}
                setLegalHold={(checked) =>
                  this.setState((state) => ({
                    statementInProgress: state.statementInProgress.setLegalHold(
                      checked ? 1 : 0,
                    ),
                  }))
                }
                setAutoIdentifyIrs={(checked) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setAutoIdentifyIrs(
                        checked ? 1 : 0,
                      ),
                  }))
                }
                setComfortLetter={(checked) =>
                  this.setState((prevState) => ({
                    statementInProgress:
                      prevState.statementInProgress.setComfortLetter(
                        checked ? 1 : 0,
                      ),
                  }))
                }
                setDefaultScaling={(option) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setDefaultScaling(option),
                  }))
                }
                setDefaultUnit={(option) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setDefaultUnit(option),
                  }))
                }
                setFormulaOrientation={(option) =>
                  this.setState((state) => ({
                    statementInProgress:
                      state.statementInProgress.setFormulaOrientation(option),
                  }))
                }
              />
            </div>
            {showNavigationConfirm ? (
              <StatementEditCancelModal
                toggleCancelModal={this._handleModalCancel}
                onCancelAndLeavePage={this._onCancelAndLeavePage}
              />
            ) : null}
          </React.Fragment>
        </ConditionalRender>
      </Page>
    );
  }
}
StatementEdit.propTypes = {
  /** Navigate function provided by withRouter HOC */
  navigate: PropTypes.func.isRequired,
  /** Params from router containing projectId and statementId */
  params: PropTypes.object.isRequired,
  /** Func that fires action which sets the project on the homepage following statement editing */
  finishStatementEdit: PropTypes.func.isRequired,
  /** Function to update selected project from ID if it doesn't exist */
  updateSelectedProjectFromIDIfDoesNotExists: PropTypes.func.isRequired,
  /** Shows navigation confirmation modal when user tries to leave page */
  showNavigationConfirm: PropTypes.bool,
  /** Triggers navigation confirmation when user attempts to navigate away */
  triggerNavigationConfirm: PropTypes.func,
  /** Confirms navigation and allows user to leave the page */
  confirmNavigation: PropTypes.func,
  /** Cancels navigation and keeps user on current page */
  cancelNavigation: PropTypes.func,
  /** Disables navigation blocking for the component */
  disableNavigationBlocking: PropTypes.func,
};

export default withNavigationBlocking(StatementEdit);
